import { css, html, nothing, svg } from 'lit';
import { localSpaceCompassEndpoints } from '../generated/endpoint-defaults.js';
import type { ChromeString } from '../i18n/chrome-strings.js';
import { planetGlyph } from '../tokens/index.js';
import type { LocalSpaceResponse } from '../types/index.js';
import { RoxyDataElement } from '../utils/base-element.js';
import { baseStyles } from '../utils/base-styles.js';
import { ChartViewport, chartViewportStyles } from '../utils/chart-viewport.js';
import { customElement } from '../utils/define.js';
import { arcSeparation, fanOut } from '../utils/degree.js';
import { formatDateTime } from '../utils/format.js';
import { display } from '../utils/localized.js';
import { planetColor } from '../utils/planet-color.js';

type Body = LocalSpaceResponse['bodies'][number];

const SIZE = 320;
const CENTER = SIZE / 2;
const RIM = 128;
const SPOKE = 118;
/** The body glyphs ride a ring just outside the rim and the compass words a ring outside that, far enough apart that a body at a compass point never prints over its word. */
const GLYPH_R = 137;
const TICK_LABEL_R = 152;
/** A body glyph is this many em wide, measured on the rendered text, at the `.body-glyph` size the stylesheet declares. */
const GLYPH_EM = 0.95;
export const COMPASS_TYPE_SIZES = { glyph: 11 } as const;
const GLYPH_FONT = COMPASS_TYPE_SIZES.glyph;

// Compass azimuth (0 = north, clockwise) to a screen point. North is up, east
// is right, matching how the local space line is read off a real compass.
function azimuthPoint(az: number, r: number): { x: number; y: number } {
	const rad = (az * Math.PI) / 180;
	return { x: CENTER + r * Math.sin(rad), y: CENTER - r * Math.cos(rad) };
}

/** The eight principal points of the rose, each with the English SOURCE its label is looked up by. All eight move together: a rose that translated only the corners would read half in one script. */
const PRINCIPAL: ReadonlyArray<{ az: number; source: ChromeString }> = [
	{ az: 0, source: 'N' },
	{ az: 45, source: 'NE' },
	{ az: 90, source: 'E' },
	{ az: 135, source: 'SE' },
	{ az: 180, source: 'S' },
	{ az: 225, source: 'SW' },
	{ az: 270, source: 'W' },
	{ az: 315, source: 'NW' },
];

/**
 * Local space compass. Plots each body from a /astrology/local-space response as
 * a directional line radiating from the birthplace at its azimuth (0 = north,
 * clockwise), with a 16-point ring. Bodies below the horizon are dimmed. Color
 * is per body and theme-token driven.
 */
@customElement('roxy-local-space-compass')
export class RoxyLocalSpaceCompass extends RoxyDataElement<LocalSpaceResponse> {
	static endpoints = localSpaceCompassEndpoints;

	private readonly chartViewport = new ChartViewport(this);

	static styles = [
		baseStyles,
		chartViewportStyles,
		css`.wrap{background:var(--roxy-surface,#fff);width:100%;color:var(--roxy-fg,#0a0a0a);border:1px solid var(--roxy-border,#e4e4e7);border-radius:var(--roxy-radius-md,8px);padding:var(--roxy-space-lg,1.5rem);box-shadow:var(--roxy-shadow-sm);gap:var(--roxy-space-md,1rem);grid-template-columns:minmax(0,1fr);display:grid}.title{font-size:var(--roxy-text-lg,1.125rem);font-weight:var(--roxy-weight-bold,600);color:var(--roxy-primary,#0f172a);margin:0}.meta{color:var(--roxy-muted,#71717a);font-size:var(--roxy-text-sm,.875rem)}svg{width:100%;max-width:var(--roxy-chart-max-width,480px);aspect-ratio:1;height:auto;margin:0 auto;display:block}.dial{fill:none;stroke:var(--roxy-border,#e4e4e7)}.dial-fill{fill:color-mix(in srgb, var(--roxy-border,#e4e4e7) 10%, transparent);stroke:var(--roxy-border,#e4e4e7);stroke-width:1px}.cardinal-axis{stroke:var(--roxy-border,#e4e4e7);stroke-width:.5px}.tick{stroke:var(--roxy-secondary,#475569);stroke-width:.6px;opacity:.5}.compass-label{fill:var(--roxy-secondary,#475569);font-size:9px;font-weight:600;font-family:var(--roxy-font-sans)}.compass-label.cardinal,.center-dot{fill:var(--roxy-fg,#0a0a0a)}.spoke{stroke-width:1.4px}.spoke.below{stroke-dasharray:3 3;opacity:.4}.body-glyph{font-size:11px;font-weight:600;font-family:var(--roxy-font-sans)}.body-glyph.below{opacity:.45}.wrap>*{min-width:0}.table-scroll{-webkit-overflow-scrolling:touch;min-width:0;overflow-x:auto}.list{border-collapse:collapse;width:100%;font-size:var(--roxy-text-sm,.875rem)}.list th,.list td{text-align:left;border-bottom:1px solid var(--roxy-border,#e4e4e7);padding:4px 8px}.list th{color:var(--roxy-muted,#71717a);text-transform:uppercase;letter-spacing:.04em;font-weight:600;font-size:var(--roxy-text-xs,.75rem)}.list td.num{text-align:right;font-variant-numeric:tabular-nums}.body-cell{align-items:center;gap:.4rem;display:inline-flex}.body-dot{border-radius:50%;flex-shrink:0;width:10px;height:10px}.horizon-pill{border-radius:var(--roxy-radius-full,9999px);font-size:var(--roxy-text-xs,.75rem);padding:1px 7px}.horizon-pill.up{background:color-mix(in srgb, var(--roxy-success,#16a34a) 16%, transparent);color:var(--_success-fg)}.horizon-pill.down{background:color-mix(in srgb, var(--roxy-border,#e4e4e7) 55%, transparent);color:var(--roxy-fg,#0a0a0a)}.summary{color:var(--roxy-fg,#0a0a0a);font-size:var(--roxy-text-sm,.875rem);margin:0}`,
	];

	protected renderData(data: LocalSpaceResponse) {
		const bodies = data.bodies ?? [];
		const bd = data.birthDetails;
		return html`<div class="wrap" part="card"><header part="header"><h2 class="title">${this.t('Local space')}</h2>${
					bd
						? html`<div class="meta">${formatDateTime(this.effectiveLang(), bd.date, bd.time)}</div>`
						: nothing
				}</header>${this.chartViewport.renderChartViewport(this.renderDial(bodies), this.t('Local space'), this.translator)} ${data.summary ? html`<p class="summary">${data.summary}</p>` : nothing} ${this.renderList(bodies)}</div>`;
	}

	private renderDial(bodies: Body[]) {
		return html`<svg viewBox="0 0 ${SIZE} ${SIZE}" part="chart" role="img" aria-label="${this.t('Local space compass of planetary directions from the birthplace')}"><title>${this.t('Local space compass')}</title><desc>${this.t(
					'A compass centered on the birthplace. Each body is a line pointing to its azimuth, clockwise from north. Bodies below the horizon are dimmed.',
				)}</desc><circle class="dial-fill" cx="${CENTER}" cy="${CENTER}" r="${RIM}"/><circle class="dial" cx="${CENTER}" cy="${CENTER}" r="${RIM * 0.66}" stroke-width="0.5"/><circle class="dial" cx="${CENTER}" cy="${CENTER}" r="${RIM * 0.33}" stroke-width="0.5"/>${this.renderCompassRing()} ${this.renderSpokes(bodies)}<circle class="center-dot" cx="${CENTER}" cy="${CENTER}" r="2.5"/></svg>`;
	}

	private renderCompassRing() {
		const ticks = [];
		// 16-point ring: a tick every 22.5 degrees.
		for (let az = 0; az < 360; az += 22.5) {
			const outer = azimuthPoint(az, RIM);
			const inner = azimuthPoint(az, RIM - (az % 45 === 0 ? 8 : 4));
			ticks.push(
				svg`<line class="tick" x1=${inner.x} y1=${inner.y} x2=${outer.x} y2=${outer.y} />`,
			);
		}
		// Cardinal cross.
		const ns1 = azimuthPoint(0, RIM);
		const ns2 = azimuthPoint(180, RIM);
		const ew1 = azimuthPoint(90, RIM);
		const ew2 = azimuthPoint(270, RIM);
		const labels = PRINCIPAL.map(({ az, source }) => {
			const pos = azimuthPoint(az, TICK_LABEL_R);
			const cardinal = az % 90 === 0;
			return svg`<text class=${`compass-label${cardinal ? ' cardinal' : ''}`} x=${pos.x} y=${pos.y} text-anchor="middle" dominant-baseline="central">${this.t(source)}</text>`;
		});
		return svg`
			<line class="cardinal-axis" x1=${ns1.x} y1=${ns1.y} x2=${ns2.x} y2=${ns2.y} />
			<line class="cardinal-axis" x1=${ew1.x} y1=${ew1.y} x2=${ew2.x} y2=${ew2.y} />
			${ticks}${labels}`;
	}

	/**
	 * One spoke per body at its true azimuth, and its glyph on the ring outside
	 * the rim, fanned along that ring so several bodies in one quarter of the
	 * sky never print one glyph over another. The spoke never moves; the colour
	 * ties a displaced glyph back to it.
	 */
	private renderSpokes(bodies: Body[]) {
		const indexed = bodies.map((b, i) => ({ b, i }));
		const separation = arcSeparation(GLYPH_EM * GLYPH_FONT, GLYPH_R);
		return fanOut(indexed, ({ b }) => b.azimuth, separation).map(
			({ item, displayLongitude }) => {
				const { b, i } = item;
				const color = planetColor(b.planet, i);
				const below = b.aboveHorizon === false;
				const end = azimuthPoint(b.azimuth, SPOKE);
				const glyphPos = azimuthPoint(displayLongitude, GLYPH_R);
				// Response symbol first, then the shared table, then the full name. Never a
				// truncation: `North Node.slice(0, 2)` drew a compass spoke labelled "No".
				const glyph = b.symbol || planetGlyph(b.planet) || display(b, 'planet');
				const altLabel = `${b.altitude > 0 ? '+' : ''}${Math.round(b.altitude)}°`;
				return svg`<g>
				<line class=${`spoke${below ? ' below' : ''}`} stroke=${color} x1=${CENTER} y1=${CENTER} x2=${end.x} y2=${end.y}><title>${this.t('{{planet}} {{direction}} {{azimuth}}° altitude {{altitude}}', { planet: display(b, 'planet'), direction: b.compassDirection, azimuth: Math.round(b.azimuth), altitude: altLabel })}</title></line>
				<text class=${`body-glyph${below ? ' below' : ''}`} fill=${color} x=${glyphPos.x} y=${glyphPos.y} text-anchor="middle" dominant-baseline="central">${glyph}</text>
			</g>`;
			},
		);
	}

	private renderList(bodies: Body[]) {
		if (bodies.length === 0) return nothing;
		return html`<div class="table-scroll" part="table"><table class="list"><caption class="roxy-sr-only">${this.t(
					'Local space directions: each body with its compass direction, azimuth, altitude and whether it sits above or below the horizon.',
				)}</caption><thead><tr><th scope="col">${this.t('Body')}</th><th scope="col">${this.t('Direction')}</th><th scope="col" class="num">${this.t('Azimuth')}</th><th scope="col" class="num">${this.t('Altitude')}</th><th scope="col">${this.t('Horizon')}</th></tr></thead><tbody>${bodies.map((b, i) => {
					const color = planetColor(b.planet, i);
					const below = b.aboveHorizon === false;
					return html`<tr><td><span class="body-cell"><span class="body-dot" style="${`background: ${color}`}"></span> ${b.symbol ? html`${b.symbol}` : nothing}${display(b, 'planet')}</span></td><td>${b.compassDirection}</td><td class="num">${Math.round(b.azimuth)}°</td><td class="num">${b.altitude > 0 ? '+' : ''}${Math.round(b.altitude)}°</td><td><span class="${`horizon-pill ${below ? 'down' : 'up'}`}">${below ? this.t('Below') : this.t('Above')}</span></td></tr>`;
				})}</tbody></table></div>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'roxy-local-space-compass': RoxyLocalSpaceCompass;
	}
}
